import { z } from 'zod';

// =============================================================================
// ERROR CATEGORIES AND CODES
// =============================================================================

// Authentication Errors
export const AUTH_ERRORS = {
  UNAUTHORIZED: { code: 'AUTH_001', message: 'Authentication required' },
  INVALID_TOKEN: { code: 'AUTH_002', message: 'Invalid or expired token' },
  INSUFFICIENT_PERMISSIONS: { code: 'AUTH_003', message: 'Insufficient permissions' },
  SESSION_EXPIRED: { code: 'AUTH_004', message: 'Session has expired' },
  LOGIN_FAILED: { code: 'AUTH_005', message: 'Invalid email or password' },
  SIGNUP_FAILED: { code: 'AUTH_006', message: 'Account creation failed' },
  PASSWORD_RESET_FAILED: { code: 'AUTH_007', message: 'Password reset failed' }
} as const;

// Usage Limit Errors
export const USAGE_ERRORS = {
  QUOTA_EXCEEDED: { code: 'USAGE_001', message: 'Monthly query limit exceeded' },
  RATE_LIMIT: { code: 'USAGE_002', message: 'Rate limit exceeded' },
  SUBSCRIPTION_EXPIRED: { code: 'USAGE_003', message: 'Subscription expired' },
  PLAN_LIMIT_REACHED: { code: 'USAGE_004', message: 'Plan limit reached' },
  PAYMENT_REQUIRED: { code: 'USAGE_005', message: 'Payment required to continue' }
} as const;

// Document Processing Errors
export const DOCUMENT_ERRORS = {
  UNSUPPORTED_FORMAT: { code: 'DOC_001', message: 'Unsupported file format' },
  FILE_TOO_LARGE: { code: 'DOC_002', message: 'File size exceeds limit' },
  EXTRACTION_FAILED: { code: 'DOC_003', message: 'Text extraction failed' },
  OCR_FAILED: { code: 'DOC_004', message: 'OCR processing failed' },
  UPLOAD_FAILED: { code: 'DOC_005', message: 'File upload failed' },
  PROCESSING_FAILED: { code: 'DOC_006', message: 'Document processing failed' },
  DOCUMENT_NOT_FOUND: { code: 'DOC_007', message: 'Document not found' },
  DELETE_FAILED: { code: 'DOC_008', message: 'Failed to delete document' }
} as const;

// Search and AI Errors
export const AI_ERRORS = {
  NO_RESULTS: { code: 'AI_001', message: 'No relevant information found' },
  EMBEDDING_FAILED: { code: 'AI_002', message: 'Failed to generate embeddings' },
  LLM_ERROR: { code: 'AI_003', message: 'Language model request failed' },
  CONTEXT_TOO_LONG: { code: 'AI_004', message: 'Query context exceeds limits' },
  SEARCH_FAILED: { code: 'AI_005', message: 'Search operation failed' },
  RESPONSE_GENERATION_FAILED: { code: 'AI_006', message: 'Failed to generate response' }
} as const;

// Network and System Errors
export const SYSTEM_ERRORS = {
  NETWORK_ERROR: { code: 'SYS_001', message: 'Network connection failed' },
  SERVER_ERROR: { code: 'SYS_002', message: 'Internal server error' },
  SERVICE_UNAVAILABLE: { code: 'SYS_003', message: 'Service temporarily unavailable' },
  TIMEOUT: { code: 'SYS_004', message: 'Request timed out' },
  DATABASE_ERROR: { code: 'SYS_005', message: 'Database operation failed' },
  VALIDATION_ERROR: { code: 'SYS_006', message: 'Input validation failed' }
} as const;

// Billing and Subscription Errors
export const BILLING_ERRORS = {
  PAYMENT_FAILED: { code: 'BILL_001', message: 'Payment processing failed' },
  SUBSCRIPTION_NOT_FOUND: { code: 'BILL_002', message: 'Subscription not found' },
  PLAN_CHANGE_FAILED: { code: 'BILL_003', message: 'Plan change failed' },
  BILLING_INFO_INVALID: { code: 'BILL_004', message: 'Invalid billing information' },
  CHECKOUT_FAILED: { code: 'BILL_005', message: 'Checkout session creation failed' }
} as const;

// =============================================================================
// ERROR SCHEMAS AND TYPES
// =============================================================================

export const ErrorSeveritySchema = z.enum(['low', 'medium', 'high', 'critical']);
export type ErrorSeverity = z.infer<typeof ErrorSeveritySchema>;

export const ErrorCategorySchema = z.enum([
  'authentication',
  'authorization',
  'validation',
  'network',
  'server',
  'client',
  'business',
  'system'
]);
export type ErrorCategory = z.infer<typeof ErrorCategorySchema>;

export const AppErrorSchema = z.object({
  code: z.string(),
  message: z.string(),
  category: ErrorCategorySchema,
  severity: ErrorSeveritySchema,
  details: z.record(z.unknown()).optional(),
  timestamp: z.date(),
  userMessage: z.string().optional(), // User-friendly message
  actionable: z.boolean().default(false), // Whether user can take action
  retryable: z.boolean().default(false), // Whether operation can be retried
  stack: z.string().optional() // Stack trace for debugging
});
export type AppError = z.infer<typeof AppErrorSchema>;

export const ErrorContextSchema = z.object({
  userId: z.string().optional(),
  sessionId: z.string().optional(),
  requestId: z.string().optional(),
  userAgent: z.string().optional(),
  url: z.string().optional(),
  component: z.string().optional(),
  action: z.string().optional(),
  metadata: z.record(z.unknown()).optional()
});
export type ErrorContext = z.infer<typeof ErrorContextSchema>;

// =============================================================================
// ERROR FACTORY FUNCTIONS
// =============================================================================

export const createAppError = (
  errorDef: { code: string; message: string },
  options: {
    category: ErrorCategory;
    severity: ErrorSeverity;
    details?: Record<string, unknown>;
    userMessage?: string;
    actionable?: boolean;
    retryable?: boolean;
    stack?: string;
  }
): AppError => ({
  code: errorDef.code,
  message: errorDef.message,
  category: options.category,
  severity: options.severity,
  details: options.details,
  timestamp: new Date(),
  userMessage: options.userMessage || errorDef.message,
  actionable: options.actionable || false,
  retryable: options.retryable || false,
  stack: options.stack
});

// Convenience functions for common error types
export const createAuthError = (
  errorDef: { code: string; message: string },
  details?: Record<string, unknown>,
  userMessage?: string
): AppError => createAppError(errorDef, {
  category: 'authentication',
  severity: 'medium',
  details,
  userMessage,
  actionable: true,
  retryable: false
});

export const createUsageError = (
  errorDef: { code: string; message: string },
  details?: Record<string, unknown>,
  userMessage?: string
): AppError => createAppError(errorDef, {
  category: 'business',
  severity: 'medium',
  details,
  userMessage,
  actionable: true,
  retryable: false
});

export const createDocumentError = (
  errorDef: { code: string; message: string },
  details?: Record<string, unknown>,
  userMessage?: string
): AppError => createAppError(errorDef, {
  category: 'client',
  severity: 'medium',
  details,
  userMessage,
  actionable: true,
  retryable: true
});

export const createAIError = (
  errorDef: { code: string; message: string },
  details?: Record<string, unknown>,
  userMessage?: string
): AppError => createAppError(errorDef, {
  category: 'server',
  severity: 'medium',
  details,
  userMessage,
  actionable: false,
  retryable: true
});

export const createSystemError = (
  errorDef: { code: string; message: string },
  details?: Record<string, unknown>,
  userMessage?: string
): AppError => createAppError(errorDef, {
  category: 'system',
  severity: 'high',
  details,
  userMessage,
  actionable: false,
  retryable: true
});

export const createBillingError = (
  errorDef: { code: string; message: string },
  details?: Record<string, unknown>,
  userMessage?: string
): AppError => createAppError(errorDef, {
  category: 'business',
  severity: 'medium',
  details,
  userMessage,
  actionable: true,
  retryable: false
});

// =============================================================================
// USER-FRIENDLY ERROR MESSAGES
// =============================================================================

export const getUserFriendlyMessage = (error: AppError): string => {
  if (error.userMessage) {
    return error.userMessage;
  }

  // Fallback to generic messages based on category
  switch (error.category) {
    case 'authentication':
      return 'Please log in to continue.';
    case 'authorization':
      return 'You don\'t have permission to perform this action.';
    case 'validation':
      return 'Please check your input and try again.';
    case 'network':
      return 'Connection problem. Please check your internet and try again.';
    case 'server':
      return 'Something went wrong on our end. Please try again in a moment.';
    case 'business':
      return 'This action cannot be completed due to account limitations.';
    default:
      return 'An unexpected error occurred. Please try again.';
  }
};

export const getRetryMessage = (error: AppError): string => {
  if (!error.retryable) {
    return '';
  }

  switch (error.category) {
    case 'network':
      return 'Check your connection and try again.';
    case 'server':
      return 'This might be temporary. Try again in a few moments.';
    case 'system':
      return 'Please wait a moment and try again.';
    default:
      return 'You can try this action again.';
  }
};

export const getActionableMessage = (error: AppError): string => {
  if (!error.actionable) {
    return '';
  }

  switch (error.code) {
    case AUTH_ERRORS.UNAUTHORIZED.code:
      return 'Please log in to your account.';
    case AUTH_ERRORS.SESSION_EXPIRED.code:
      return 'Please log in again.';
    case USAGE_ERRORS.QUOTA_EXCEEDED.code:
      return 'Upgrade your plan to continue.';
    case USAGE_ERRORS.PAYMENT_REQUIRED.code:
      return 'Update your payment method.';
    case DOCUMENT_ERRORS.FILE_TOO_LARGE.code:
      return 'Try uploading a smaller file.';
    case DOCUMENT_ERRORS.UNSUPPORTED_FORMAT.code:
      return 'Please upload a PDF, DOCX, PNG, or JPG file.';
    default:
      return '';
  }
};